import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { platform2faService } from '../../../services/apiService';
import { ui } from './authFieldStyles';

/**
 * Reusable enable-2FA flow (self-service, session-guarded): generate a secret -> add to an
 * authenticator -> confirm a code -> enabled. Used in first-login setup and in Security settings.
 * No QR renderer is bundled in this app, so the secret + otpauth:// link are shown for manual add.
 */
const Enable2faPanel = ({ onDone, onCancel }) => {
  const [phase, setPhase] = useState('start'); // 'start' | 'secret' | 'done'
  const [secret, setSecret] = useState('');
  const [otpauth, setOtpauth] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const begin = async () => {
    setErr('');
    setLoading(true);
    try {
      const res = await platform2faService.enable();
      setSecret(res.data.secret);
      setOtpauth(res.data.otpauth_url);
      setPhase('secret');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Could not start 2FA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!/^\d{6}$/.test(code)) {
      setErr('Enter the 6-digit code from your app.');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      await platform2faService.verifyEnable({ code });
      setPhase('done');
    } catch (e2) {
      setErr(e2.response?.data?.error?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'start') {
    return (
      <>
        <h1 style={ui.heading}>Set up two-factor authentication</h1>
        <p style={ui.sub}>Generate a secret to add to your authenticator app (Google Authenticator, Authy, 1Password, etc.).</p>
        {err && <div style={ui.errBanner}><Icon name="AlertCircle" size={14} color="#DC2626" />{err}</div>}
        <button type="button" disabled={loading} onClick={begin} style={{ ...ui.cta, ...(loading ? ui.ctaDisabled : {}) }}>
          {loading ? 'Generating…' : 'Generate secret'}
        </button>
        {onCancel && <button type="button" onClick={onCancel} disabled={loading} style={ui.secondaryBtn}>Cancel</button>}
      </>
    );
  }

  if (phase === 'secret') {
    return (
      <>
        <h1 style={ui.heading}>Add to your authenticator</h1>
        <p style={ui.sub}>Add this secret to your authenticator app, then enter the current 6-digit code to confirm.</p>
        <div style={{ marginBottom: '10px' }}>
          <label style={ui.label}>Secret key</label>
          <div style={ui.secretBox}>{secret}</div>
        </div>
        <div style={{ marginBottom: '18px' }}>
          <a href={otpauth} style={{ ...ui.linkBtn, wordBreak: 'break-all' }}>Open otpauth:// link</a>
        </div>
        <form onSubmit={verify} noValidate>
          {err && <div style={ui.errBanner}><Icon name="AlertCircle" size={14} color="#DC2626" />{err}</div>}
          <div style={ui.field}>
            <label style={ui.label} htmlFor="enable-code">Verification code</label>
            <input
              id="enable-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (err) setErr(''); }}
              placeholder="000000"
              autoFocus
              style={{ ...ui.input, ...ui.codeInput, ...(err ? ui.inputErr : {}) }}
            />
          </div>
          <button type="submit" disabled={loading} style={{ ...ui.cta, ...(loading ? ui.ctaDisabled : {}) }}>
            {loading ? 'Verifying…' : 'Verify & enable'}
          </button>
        </form>
      </>
    );
  }

  // done
  return (
    <>
      <div style={ui.okBanner}><Icon name="CheckCircle" size={16} color="#15803D" />Two-factor authentication is enabled.</div>
      <p style={ui.sub}>You'll be asked for a code from your authenticator on future logins.</p>
      <button type="button" onClick={onDone} style={ui.cta}>Continue</button>
    </>
  );
};

export default Enable2faPanel;
